import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { ActiveBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import HeroSlideForm, {
  emptyHeroSlideForm,
  formToHeroSlideFormData,
  heroSlideToForm,
} from '../components/forms/HeroSlideForm';
import {
  listHeroSlides,
  createHeroSlide,
  updateHeroSlide,
  deleteHeroSlide,
} from '../services/heroSlideService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/format';

export default function HeroSlidesPage() {
  const { showToast } = useToast();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyHeroSlideForm);

  const fetchSlides = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listHeroSlides();
      setSlides(res.data || []);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyHeroSlideForm);
    setModalOpen(true);
  };

  const openEdit = (slide) => {
    setEditing(slide);
    setForm(heroSlideToForm(slide));
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    if (!form.productId || !form.link.trim()) {
      showToast('Please select a category and product', 'error');
      return;
    }
    if (!form.existingImage && !(form.newImageFiles || []).length) {
      showToast('Slide image is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = formToHeroSlideFormData(form);
      if (editing) {
        await updateHeroSlide(editing._id, payload);
        showToast('Hero slide updated successfully');
      } else {
        await createHeroSlide(payload);
        showToast('Hero slide created successfully');
      }
      setModalOpen(false);
      fetchSlides();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slide) => {
    if (!window.confirm(`Delete slide "${slide.title}"?`)) return;
    try {
      await deleteHeroSlide(slide._id);
      showToast('Hero slide deleted');
      fetchSlides();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    }
  };

  return (
    <div>
      <PageHeader
        title="Hero Slides"
        description="Manage homepage carousel slides"
        action={
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Add Slide
          </button>
        }
      />

      <div className="card">
        {loading ? (
          <TableSkeleton cols={6} />
        ) : slides.length === 0 ? (
          <EmptyState title="No hero slides yet" action={{ label: 'Add Slide', onClick: openCreate }} />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Slide</th>
                  <th>Link</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slides.map((slide) => (
                  <tr key={slide._id}>
                    <td>
                      <div className="cell-product">
                        {slide.image && <img src={slide.image} alt="" className="cell-thumb" />}
                        <div>
                          <span className="cell-title">{slide.title}</span>
                          {slide.subtitle && (
                            <p className="cell-subtitle">{slide.subtitle}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="cell-truncate">{slide.link}</td>
                    <td>{slide.sortOrder ?? 0}</td>
                    <td>
                      <ActiveBadge active={slide.isActive !== false} />
                    </td>
                    <td>{formatDate(slide.updatedAt || slide.createdAt)}</td>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(slide)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => handleDelete(slide)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Hero Slide' : 'Create Hero Slide'}
        size="md"
      >
        <form onSubmit={handleSave}>
          <HeroSlideForm form={form} onChange={setForm} />
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
